import { compareAsc } from "date-fns";
import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import jwtDecode, { type JwtPayload } from "jwt-decode";

import { type AuthData, AuthDataSchema, type AuthToken } from "./types";

export const authDataAtom = atomWithStorage<AuthData | null>("authData", null);
export const impersonatorAuthDataAtom = atomWithStorage<AuthData | null>("impersonatorAuthDataAtom", null);

export const setAuthData = (data: AuthData | null) => {
  if (data === null) {
    localStorage.removeItem("authData");
    return;
  }

  localStorage.setItem("authData", JSON.stringify(data));
};

export function getAuthData(require: true): AuthData;
export function getAuthData(require?: boolean): AuthData | undefined;
export function getAuthData(require = false): AuthData | undefined {
  const data = localStorage.getItem("authData");
  if (data) {
    const parsedData = AuthDataSchema.safeParse(JSON.parse(data));
    if (parsedData.success) {
      return parsedData.data;
    }
  }

  if (require) {
    throw new Error("Token contained in authentication data was unexpectedly null.");
  }
  return undefined;
}

export function getAuthToken(require: true): AuthToken;
export function getAuthToken(require?: boolean): AuthToken | undefined;
export function getAuthToken(require?: boolean) {
  return getAuthData(require)?.token;
}

export function getAccessToken(require: true): string;
export function getAccessToken(require?: boolean): string | undefined;
export function getAccessToken(require = false) {
  return getAuthToken(require)?.accessToken;
}

interface Jwt extends JwtPayload {
  exp: number;
  iat: number;
  sub: string;
  /**
   * The claim the API authorizes on: `JwtStrategy.validate` resolves the account with
   * `getUserAccountByEmail(payload.email)` and 401s when there is no match, so every token the
   * server accepts carries it. Optional here because `jwtDecode` will happily decode one that
   * does not, and callers have to handle that rather than assume it.
   *
   * Note this is *not* `sub`: `sub` comes from the identity provider and has no relation to
   * `UserAccount.id`, which is a QuoteWell database UUID.
   */
  email?: string;
  impersonatedById: string | null;
  impersonatedByEmail: string | null;
}

const getJwtData = (accessToken: string) => {
  try {
    const decoded = jwtDecode<Jwt>(accessToken);
    // Convert seconds to MS for date constructor
    const issuedAt = new Date(decoded.iat * 1000);
    const expiresAt = new Date(decoded.exp * 1000);
    const now = new Date();

    return {
      ...decoded,
      issuedAt,
      expiresAt,
      hasExpired: compareAsc(now, expiresAt) >= 0,
    };
  } catch {
    return null;
  }
};

export const getIsUserAuthenticated = (): boolean => {
  const token = getAuthToken();
  if (!token) {
    return false;
  }
  const jwtData = getJwtData(token.accessToken);
  return jwtData ? !jwtData.hasExpired : false;
};

export const jwtDataAtom = atom((get) => {
  const authData = get(authDataAtom);
  return authData ? getJwtData(authData.token.accessToken) : null;
});

export const authTokenValidatedAtom = atom<boolean>(false);

export const getAuthRequestHeaders = () => {
  const accessToken = getAccessToken(true);

  return {
    Authorization: `Bearer ${accessToken}`,
  };
};
